import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowDown, ArrowRight, BarChart3, Bot, Brain, Building2,
  Code2, Globe2, LineChart, Network, Search, Server,
  ShoppingBag, Sparkles, Target, TrendingUp, Zap, Eye,
  ChevronRight, Shield, Users, GitBranch, MonitorSmartphone,
  Cpu, Database, Layers, Upload, FileCode, ScanLine, Workflow,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LandingPageProps {
  onEnterApp?: () => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TOTAL_SECTIONS = 12;

const SECTION_LABELS = ['首页', '行为迁徙', '市场规模', '客户群体', 'AI 监控', '智能引擎', '外链推广', '站点优化', '品牌画像', 'GEO Agent', '功能亮点', '开始使用'];

const FAV = (domain: string) => `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

const DOMESTIC_PLATFORMS = [
  { name: 'DeepSeek', abbr: 'DS', color: 'from-blue-500 to-blue-600', logo: FAV('deepseek.com') },
  { name: 'Kimi', abbr: 'KM', color: 'from-orange-400 to-orange-500', logo: FAV('kimi.moonshot.cn') },
  { name: '豆包', abbr: '豆', color: 'from-teal-500 to-teal-600', logo: FAV('doubao.com') },
  { name: '文心', abbr: '文', color: 'from-indigo-500 to-indigo-600', logo: FAV('yiyan.baidu.com') },
  { name: '通义', abbr: '通', color: 'from-violet-500 to-violet-600', logo: FAV('tongyi.aliyun.com') },
  { name: '智谱', abbr: '智', color: 'from-sky-500 to-sky-600', logo: FAV('zhipuai.cn') },
  { name: '混元', abbr: '混', color: 'from-green-500 to-green-600', logo: FAV('hunyuan.tencent.com') },
  { name: '360AI', abbr: '360', color: 'from-red-500 to-red-600', logo: FAV('ai.360.cn') },
  { name: '天工', abbr: '天', color: 'from-amber-500 to-amber-600', logo: FAV('tiangong.cn') },
  { name: '海螺', abbr: '螺', color: 'from-pink-500 to-pink-600', logo: FAV('hailuoai.com') },
];

const GLOBAL_PLATFORMS = [
  { name: 'ChatGPT', abbr: 'GP', color: 'from-emerald-600 to-emerald-700', logo: FAV('chatgpt.com') },
  { name: 'Perplexity', abbr: 'PP', color: 'from-teal-600 to-teal-700', logo: FAV('perplexity.ai') },
  { name: 'Gemini', abbr: 'GM', color: 'from-blue-600 to-blue-700', logo: FAV('gemini.google.com') },
  { name: 'Claude', abbr: 'CL', color: 'from-orange-500 to-orange-600', logo: FAV('claude.ai') },
  { name: 'Bing AI', abbr: 'BA', color: 'from-blue-500 to-blue-600', logo: FAV('bing.com') },
  { name: 'Grok', abbr: 'GK', color: 'from-zinc-600 to-zinc-700', logo: FAV('x.ai') },
  { name: 'Meta AI', abbr: 'MT', color: 'from-indigo-600 to-indigo-700', logo: FAV('ai.meta.com') },
  { name: 'Copilot', abbr: 'CP', color: 'from-sky-600 to-sky-700', logo: FAV('copilot.microsoft.com') },
  { name: 'You.com', abbr: 'YO', color: 'from-purple-500 to-purple-600', logo: FAV('you.com') },
  { name: 'Brave AI', abbr: 'BR', color: 'from-rose-500 to-rose-600', logo: FAV('brave.com') },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

const DotGrid: React.FC = () => (
  <div
    className="absolute inset-0 pointer-events-none opacity-[0.035]"
    aria-hidden="true"
    style={{
      backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
      backgroundSize: '28px 28px',
    }}
  />
);

const GradientOrbs: React.FC = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
    <div className="absolute top-1/4 -left-40 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-indigo-100/50 to-cyan-100/30 blur-3xl" />
    <div className="absolute bottom-1/4 -right-40 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-violet-100/40 to-fuchsia-100/25 blur-3xl" />
  </div>
);

const SectionNumber: React.FC<{ n: number }> = ({ n }) => (
  <div
    className="absolute right-8 bottom-8 text-[14vw] font-black leading-none text-zinc-900/[0.025] select-none pointer-events-none tracking-tighter"
    aria-hidden="true"
  >
    {String(n).padStart(2, '0')}
  </div>
);

// ─── SectionShell ── Standard light-bg page template ────────────────────────
// Usage: <SectionShell index={N} active={active} label="页面标题">…content…</SectionShell>
// index is 0-based position in LandingPage's render tree → displays as 0-padded (index+1)
const SectionShell: React.FC<{
  index: number;
  active: boolean;
  label: string;
  orbs?: boolean;
  maxWidth?: string;
  /** Extra classes applied to the outer <section> (e.g. "px-8" to override default px-6) */
  className?: string;
  /** Extra classes applied to the animated inner wrapper div */
  innerClassName?: string;
  children: React.ReactNode;
}> = ({ index, active, label, orbs = false, maxWidth = 'max-w-5xl', className = 'px-6', innerClassName = '', children }) => (
  <section
    className={`relative h-screen w-full flex flex-col items-center justify-center overflow-hidden ${className}`}
    aria-label={label}
  >
    <DotGrid />
    {orbs && <GradientOrbs />}
    <SectionNumber n={index + 1} />
    <div className={`relative z-10 w-full ${maxWidth} ${innerClassName} transition-all duration-700 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      {children}
    </div>
  </section>
);

// ─── App Mockups ────────────────────────────────────────────────────────────

const BrowserFrame: React.FC<{ url: string; children: React.ReactNode; className?: string }> = ({ url, children, className = '' }) => (
  <div className={`rounded-2xl overflow-hidden ring-1 ring-zinc-200/80 shadow-[0_8px_48px_-8px_rgba(0,0,0,0.13),0_0_0_1px_rgba(0,0,0,0.04)] flex flex-col ${className}`}>
    <div className="flex items-center gap-1.5 px-3 py-2.5 bg-zinc-50 border-b border-zinc-200 shrink-0">
      <div className="flex items-center gap-1.5">
        <div className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" aria-hidden="true" />
        <div className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" aria-hidden="true" />
        <div className="h-2.5 w-2.5 rounded-full bg-[#28c840]" aria-hidden="true" />
      </div>
      <div className="flex-1 mx-2.5">
        <div className="bg-white rounded-full px-3 py-1 text-[10px] text-zinc-400 truncate ring-1 ring-zinc-200">
          {url}
        </div>
      </div>
    </div>
    <div className="flex-1 min-h-0 overflow-hidden">
      {children}
    </div>
  </div>
);

const OverviewMockup: React.FC = () => (
  <div className="flex-1 min-h-0 overflow-hidden">
    <img
      src="/assets/pitch/overview3.png"
      alt="PEEC AI platform overview"
      className="w-full h-full object-cover object-top"
    />
  </div>
);


const CitationFeedMockup: React.FC = () => (
  <div className="bg-white h-full flex flex-col">
    {/* Header */}
    <div className="flex items-center justify-between px-3.5 py-2 border-b border-zinc-100 shrink-0">
      <div className="text-[11px] font-bold text-zinc-900">AI 引用分析</div>
      <div className="flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" />
        <span className="text-[9px] text-zinc-400">实时追踪</span>
      </div>
    </div>
    {/* Tabs */}
    <div className="flex px-3.5 border-b border-zinc-100 gap-px shrink-0">
      {['实时动态', '竞品对比', '来源分析'].map((t, i) => (
        <div key={t} className={`px-2 py-1.5 text-[8px] font-medium border-b-[1.5px] ${i === 0 ? 'text-indigo-600 border-indigo-500' : 'text-zinc-400 border-transparent'}`}>{t}</div>
      ))}
    </div>
    {/* Feed */}
    <div className="flex-1 overflow-hidden">
      {[
        { platform: 'GPT-4o', region: 'US', time: '3m', color: 'from-emerald-500 to-emerald-600', query: 'What is the best stock trading platform?', sources: ['bankrate.com', 'reddit.com'] },
        { platform: 'Claude 3', region: 'HK', time: '11m', color: 'from-orange-400 to-orange-500', query: 'Which brokerage has the lowest fees?', sources: ['nerdwallet.com'] },
        { platform: 'Perplexity', region: 'UK', time: '24m', color: 'from-teal-500 to-teal-600', query: 'Best charting tools for technical analysis 2025', sources: ['tradingview.com'] },
        { platform: 'Gemini', region: 'SG', time: '38m', color: 'from-blue-500 to-blue-600', query: 'How to invest in stocks for beginners?', sources: ['investopedia.com', 'cnbc.com'] },
      ].map((c, i) => (
        <div key={i} className="flex gap-2.5 px-3.5 py-2 border-b border-zinc-50 last:border-0">
          <div className={`h-6 w-6 rounded-lg bg-gradient-to-br ${c.color} flex items-center justify-center text-white text-[8px] font-bold shrink-0 mt-0.5`}>
            {c.platform.slice(0, 2)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-[9px] font-semibold text-zinc-700">{c.platform}</span>
              <span className="rounded px-1 py-px bg-zinc-100 text-[7px] text-zinc-500">{c.region}</span>
              <span className="text-[7px] text-zinc-300 ml-auto tabular-nums">{c.time} ago</span>
            </div>
            <div className="text-[9px] text-zinc-600 leading-snug mb-1 truncate">{c.query}</div>
            <div className="flex gap-1 flex-wrap">
              {c.sources.map(s => (
                <span key={s} className="rounded-full bg-indigo-50 px-1.5 py-px text-[7px] text-indigo-500 ring-1 ring-indigo-100/80">{s}</span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ChannelMockup: React.FC = () => (
  <div className="bg-white h-full flex flex-col">
    {/* Header */}
    <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-zinc-100 shrink-0">
      <div>
        <div className="text-[11px] font-bold text-zinc-900">投放渠道分析</div>
        <div className="text-[8px] text-zinc-400 mt-px">已分析 2,847 个候选渠道</div>
      </div>
      <div className="rounded-full bg-indigo-600 px-2 py-0.5 text-[8px] font-semibold text-white">34 推荐</div>
    </div>
    {/* Chart */}
    <div className="flex-1 px-3.5 py-2.5 overflow-hidden">
      <div className="text-[8px] font-semibold text-zinc-400 mb-2.5 uppercase tracking-wider">渠道类型 · AI 引用效率</div>
      <div className="space-y-2">
        {[
          { label: '精选技术博客', pct: 92, color: 'bg-gradient-to-r from-indigo-500 to-indigo-400', badge: true },
          { label: '垂直行业媒体', pct: 78, color: 'bg-gradient-to-r from-violet-500 to-violet-400', badge: true },
          { label: '权威问答平台', pct: 71, color: 'bg-gradient-to-r from-cyan-500 to-cyan-400', badge: false },
          { label: 'GitHub / 开源', pct: 65, color: 'bg-gradient-to-r from-emerald-500 to-emerald-400', badge: false },
          { label: '企业官方博客', pct: 54, color: 'bg-zinc-200', badge: false },
        ].map(b => (
          <div key={b.label}>
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[9px] text-zinc-600 flex items-center gap-1">
                {b.label}
                {b.badge && <span className="rounded-full bg-indigo-50 px-1 py-px text-[7px] font-semibold text-indigo-600">推荐</span>}
              </span>
              <span className="text-[9px] font-bold text-zinc-800 tabular-nums">{b.pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
              <div className={`h-full rounded-full ${b.color}`} style={{ width: `${b.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
    {/* Bottom metric */}
    <div className="mx-3.5 mb-3 shrink-0 rounded-xl bg-gradient-to-r from-indigo-50 to-violet-50 px-4 py-2.5 ring-1 ring-indigo-100/80 flex items-center justify-between">
      <div>
        <div className="text-[7px] text-indigo-400 uppercase tracking-wider mb-0.5">平均投放成本节省</div>
        <div className="text-[22px] font-black text-indigo-700 tabular-nums leading-none">−62%</div>
      </div>
      <div className="text-right">
        <div className="text-[8px] text-zinc-400">相比自主探索</div>
        <div className="text-[10px] font-bold text-emerald-600 mt-0.5">↑ ROI 2.6×</div>
      </div>
    </div>
  </div>
);

const SiteOptMockup: React.FC = () => (
  <div className="bg-white h-full flex flex-col">
    {/* Header */}
    <div className="flex items-center justify-between px-3.5 py-2 border-b border-zinc-100 shrink-0">
      <div className="text-[11px] font-bold text-zinc-900">Owned — On-page</div>
      <div className="text-[9px] text-zinc-400">5 recommendations</div>
    </div>
    {/* Filter tabs */}
    <div className="flex gap-px px-3.5 py-1.5 border-b border-zinc-100 shrink-0">
      {['Overview', 'Homepage', 'How-To', 'Article', 'Listicle'].map((t, i) => (
        <div key={t} className={`shrink-0 px-2 py-0.5 rounded text-[8px] font-medium ${i === 0 ? 'bg-indigo-600 text-white' : 'text-zinc-400'}`}>{t}</div>
      ))}
    </div>
    {/* Recommendations */}
    <div className="flex-1 px-3 py-2 space-y-2 overflow-hidden">
      {[
        { priority: 'Med', type: 'How-To Guide', text: 'Create content similar to "Guide using tradingview" and mention your brand favorably in how-to guides.' },
        { priority: 'Low', type: 'Article', text: 'Create content similar to Trading and Investing in Stocks on etoro.com and other top-performing articles.' },
        { priority: 'Low', type: 'Homepage', text: 'Incorporate "Stocks crypto etfs one powerful" into the copy of your homepage.' },
      ].map((r, i) => (
        <div key={i} className="rounded-lg border border-zinc-100 p-2 bg-zinc-50/50">
          <div className="flex items-center gap-1.5 mb-1">
            <span className={`rounded-full px-1.5 py-px text-[7px] font-bold ${r.priority === 'Med' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-500'}`}>{r.priority}</span>
            <span className="rounded-full bg-indigo-50 px-1.5 py-px text-[7px] text-indigo-600">{r.type}</span>
          </div>
          <div className="text-[9px] text-zinc-600 leading-relaxed line-clamp-2">{r.text}</div>
          <div className="flex gap-1 mt-1.5">
            {['Done', 'Decline', 'Todo'].map(a => (
              <button key={a} className="px-1.5 py-px rounded text-[7px] text-zinc-500 bg-white ring-1 ring-zinc-200">{a}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── Sections ────────────────────────────────────────────────────────────────

const Hero: React.FC<{ active: boolean; onEnterApp?: () => void; onNext: () => void }> = ({ active, onEnterApp, onNext }) => (
  <section className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden pt-16" aria-label="首页">
    <DotGrid />
    <GradientOrbs />

    <div className={`relative z-10 w-full transition-all duration-700 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-6 items-center">

        {/* Left: copy */}
        <div className="md:col-span-2 pl-8 md:pl-12">
          {/* Eyebrow */}
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-zinc-50 px-4 py-1.5 text-xs font-medium text-zinc-600 ring-1 ring-zinc-200">
            <Sparkles size={12} className="text-indigo-500" aria-hidden="true" />
            全球首个覆盖中国内地与全球的 AI 搜索优化平台
            <Sparkles size={12} className="text-cyan-500" aria-hidden="true" />
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-[3.6rem] font-black tracking-tighter text-zinc-950 text-balance leading-[1.08] max-w-lg">
            让 AI 搜索
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-violet-500 to-cyan-500">
              主动推荐你的品牌
            </span>
          </h1>

          {/* Sub */}
          <p className="mt-4 max-w-sm text-sm text-zinc-500 text-balance leading-relaxed">
            覆盖 20+ 国内外主流 AI 平台 · 实时监控 · 智能引擎分析 · 精准投放 · 代码级自动优化
          </p>

          {/* CTAs */}
          <div className="mt-7 flex items-center gap-3">
            <button
              onClick={onEnterApp}
              className="inline-flex h-11 items-center gap-2 rounded-full bg-zinc-950 px-6 text-sm font-semibold text-white outline-none transition-colors hover:bg-zinc-800 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-2"
            >
              立即体验 <ArrowRight size={15} aria-hidden="true" />
            </button>
            <button
              onClick={onNext}
              className="inline-flex h-11 items-center gap-2 rounded-full bg-white px-6 text-sm font-medium text-zinc-700 outline-none ring-1 ring-zinc-200 transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-2"
            >
              了解产品 <ChevronRight size={14} aria-hidden="true" />
            </button>
          </div>

          {/* Stats */}
          <div className="mt-8 grid grid-cols-2 gap-2">
            {[
              { label: '覆盖 AI 平台', value: '20+' },
              { label: '数据实时更新', value: '7×24h' },
              { label: '核心功能模块', value: '6 大' },
              { label: '全链路覆盖', value: '监控→优化→分发' },
            ].map(stat => (
              <div key={stat.label} className="rounded-xl bg-white px-3 py-2 text-center shadow-card ring-1 ring-zinc-100">
                <div className="text-lg font-black tracking-tighter text-zinc-950 tabular-nums">{stat.value}</div>
                <div className="text-[9px] text-zinc-400 mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: browser mockup — bleeds to right edge */}
        <div className="hidden md:col-span-5 md:block pr-0">
          <div className="aspect-video">
            <BrowserFrame url="app.yuanji.ai/overview" className="h-full rounded-r-none">
              <OverviewMockup />
            </BrowserFrame>
          </div>
        </div>

      </div>
    </div>
  </section>
);

const CustomerSegments: React.FC<{ active: boolean; index: number }> = ({ active, index }) => {
  const segments = [
    {
      icon: Building2,
      title: '国内中小企业',
      desc: '在 DeepSeek、Kimi、豆包、文心一言等国内主流 AI 助手中建立品牌认知，把握 AI 搜索流量新红利。',
      tags: [
        { name: 'DeepSeek', logo: FAV('deepseek.com') },
        { name: 'Kimi', logo: FAV('kimi.moonshot.cn') },
        { name: '豆包', logo: FAV('doubao.com') },
        { name: '通义', logo: FAV('tongyi.aliyun.com') },
      ],
      accent: 'from-indigo-50 to-indigo-100/40',
      iconColor: 'text-indigo-600',
      tagColor: 'bg-indigo-50 text-indigo-600',
    },
    {
      icon: ShoppingBag,
      title: '海外电商独立站',
      desc: '在 ChatGPT Shopping、Perplexity、Bing AI 等全球 AI 平台中占领商品搜索位置，降低获客成本。',
      tags: [
        { name: 'ChatGPT', logo: FAV('chatgpt.com') },
        { name: 'Perplexity', logo: FAV('perplexity.ai') },
        { name: 'Gemini', logo: FAV('gemini.google.com') },
        { name: 'Copilot', logo: FAV('copilot.microsoft.com') },
      ],
      accent: 'from-emerald-50 to-emerald-100/40',
      iconColor: 'text-emerald-600',
      tagColor: 'bg-emerald-50 text-emerald-600',
    },
    {
      icon: Globe2,
      title: '出海与海外客户',
      desc: '覆盖东南亚、欧美、中东等全球市场，针对不同地区 AI 搜索偏好进行本地化优化，全球曝光触手可及。',
      tags: [
        { name: 'Claude', logo: FAV('claude.ai') },
        { name: 'Grok', logo: FAV('x.ai') },
        { name: 'Meta AI', logo: FAV('ai.meta.com') },
        { name: 'You.com', logo: FAV('you.com') },
      ],
      accent: 'from-cyan-50 to-cyan-100/40',
      iconColor: 'text-cyan-600',
      tagColor: 'bg-cyan-50 text-cyan-600',
    },
  ];

  return (
    <section className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden px-6" aria-label="客户群体">
      <DotGrid />
      <SectionNumber n={index + 1} />

      <div className={`relative z-10 w-full max-w-6xl transition-all duration-700 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="mb-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-2">我们为谁服务</p>
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-zinc-950">
            三大客户群体，全覆盖
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {segments.map((seg, i) => (
            <div
              key={seg.title}
              className={`rounded-2xl bg-gradient-to-br ${seg.accent} p-6 ring-1 ring-zinc-200 transition-all duration-500`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-card ${seg.iconColor}`}>
                <seg.icon size={22} aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold tracking-tight text-zinc-950 mb-2">{seg.title}</h3>
              <p className="text-sm text-zinc-500 leading-relaxed mb-4">{seg.desc}</p>
              <div className="flex flex-wrap gap-2">
                {seg.tags.map(tag => (
                  <div key={tag.name} className={`flex flex-col items-center gap-1 rounded-xl px-2.5 py-1.5 ${seg.tagColor}`}>
                    <img src={tag.logo} alt="" aria-hidden="true" className="h-6 w-6 object-contain shrink-0" />
                    <span className="text-[10px] font-medium">{tag.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PlatformMonitoring: React.FC<{ active: boolean; index: number }> = ({ active, index }) => (
  <section className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden" aria-label="AI 平台监控">
    <DotGrid />
    <SectionNumber n={index + 1} />

    <div className={`relative z-10 w-full transition-all duration-700 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-6 items-center">

        {/* Left: copy + platforms */}
        <div className="md:col-span-2 pl-8 md:pl-12 space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-2">实时监控</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-zinc-950 text-balance mb-2">
              20+ AI 平台，一网打尽
            </h2>
            <p className="text-sm text-zinc-500 leading-relaxed">
              7×24 小时持续追踪可见度变化，品牌提及、排名波动、竞品动态实时预警
            </p>
          </div>

          {/* Domestic */}
          <div className="rounded-2xl bg-white p-3.5 ring-1 ring-zinc-200 shadow-card">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="h-1.5 w-1.5 rounded-full bg-red-400 animate-pulse" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">中国大陆</span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {DOMESTIC_PLATFORMS.map(p => (
                <div key={p.name} className="flex flex-col items-center gap-1">
                  <div className={`h-10 w-10 rounded-xl overflow-hidden shadow-sm flex items-center justify-center ${p.logo ? 'bg-white ring-1 ring-zinc-100' : `bg-gradient-to-br ${p.color}`}`}>
                    {p.logo ? (
                      <img src={p.logo} alt={p.name} className="h-7 w-7 object-contain" />
                    ) : (
                      <span className="text-white text-[11px] font-bold">{p.abbr}</span>
                    )}
                  </div>
                  <span className="text-[9px] text-zinc-400 text-center leading-tight">{p.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Global */}
          <div className="rounded-2xl bg-white p-3.5 ring-1 ring-zinc-200 shadow-card">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">全球</span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {GLOBAL_PLATFORMS.map(p => (
                <div key={p.name} className="flex flex-col items-center gap-1">
                  <div className={`h-10 w-10 rounded-xl overflow-hidden shadow-sm flex items-center justify-center ${p.logo ? 'bg-white ring-1 ring-zinc-100' : `bg-gradient-to-br ${p.color}`}`}>
                    {p.logo ? (
                      <img src={p.logo} alt={p.name} className="h-7 w-7 object-contain" />
                    ) : (
                      <span className="text-white text-[11px] font-bold">{p.abbr}</span>
                    )}
                  </div>
                  <span className="text-[9px] text-zinc-400 text-center leading-tight">{p.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Metrics */}
          <div className="flex flex-col gap-2">
            {[
              { icon: Eye, label: '品牌 · 排名 · 情感监控', color: 'text-indigo-500' },
              { icon: BarChart3, label: '日 / 周 / 月 数据粒度', color: 'text-cyan-600' },
              { icon: Zap, label: '实时异常预警', color: 'text-amber-500' },
            ].map(m => (
              <div key={m.label} className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 ring-1 ring-zinc-100 shadow-card">
                <m.icon size={13} className={m.color} aria-hidden="true" />
                <span className="text-xs text-zinc-600">{m.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: gap analysis screenshot */}
        <div className="hidden md:col-span-5 md:flex items-center">
          <div className="aspect-video w-full">
            <BrowserFrame url="app.yuanji.ai/gap-analysis" className="h-full w-full rounded-r-none">
              <div className="flex-1 min-h-0 overflow-hidden">
                <img
                  src="/assets/pitch/gap_analysis.png"
                  alt="AI 平台差距分析"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </BrowserFrame>
          </div>
        </div>

      </div>
    </div>
  </section>
);

const IntelligenceEngine: React.FC<{ active: boolean; index: number }> = ({ active, index }) => (
  <section className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden" aria-label="元基智能引擎">
    <DotGrid />
    <SectionNumber n={index + 1} />

    <div className={`relative z-10 w-full transition-all duration-700 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-6 items-center">

        {/* Left copy */}
        <div className="md:col-span-2 pl-8 md:pl-12 space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-3">元基智能引擎</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-zinc-950 leading-[1.1] text-balance mb-3">
              最低成本，<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-cyan-500">最大曝光增长</span>
            </h2>
            <p className="text-sm text-zinc-500 leading-relaxed">
              从全平台 AI 检索结果中智能分析，找到客户行业最佳 ROI 的引用来源
            </p>
          </div>

          <div className="space-y-2.5">
            {[
              { icon: Database, label: '海量数据采集', desc: '持续抓取全网 AI 引擎推荐源站数据' },
              { icon: Brain, label: '智能模式识别', desc: '分析内容形态与发布渠道效果最佳实践' },
              { icon: Target, label: '精准渠道推荐', desc: '锁定 ROI 最高的投放网站与内容策略' },
            ].map(step => (
              <div key={step.label} className="flex items-start gap-3 rounded-xl bg-white p-3.5 ring-1 ring-zinc-100 shadow-card">
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-50 to-indigo-100 text-indigo-600">
                  <step.icon size={14} aria-hidden="true" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-zinc-800">{step.label}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: prompts console */}
        <div className="hidden md:col-span-5 md:block">
          <div className="aspect-video">
            <BrowserFrame url="app.yuanji.ai/prompts" className="h-full rounded-r-none">
              <div className="flex-1 min-h-0 overflow-hidden">
                <img src="/assets/pitch/prompts.png" alt="AI 引用来源分析" className="w-full h-full object-cover object-top" />
              </div>
            </BrowserFrame>
          </div>
        </div>

      </div>
    </div>
  </section>
);

// ─── EarnedMedia ───────────────────────────────────────────────────────────

const MEDIA_PLATFORMS = [
  { name: '公众号',  logo: FAV('weixin.qq.com'),         abbr: '微' },
  { name: '知乎',    logo: FAV('zhihu.com'),              abbr: '知' },
  { name: '头条号',  logo: FAV('toutiao.com'),            abbr: '条' },
  { name: '百家号',  logo: FAV('baijiahao.baidu.com'),    abbr: '百' },
  { name: '搜狐号',  logo: FAV('sohu.com'),               abbr: '搜' },
  { name: '小红书',  logo: FAV('xiaohongshu.com'),        abbr: '红' },
  { name: '微博',    logo: FAV('weibo.com'),              abbr: '博' },
  { name: '企鹅号',  logo: FAV('om.qq.com'),              abbr: '企' },
  { name: '网易号',  logo: FAV('163.com'),                abbr: '网' },
  { name: '潎拃',    logo: FAV('thepaper.cn'),            abbr: '潎' },
];

const EarnedMedia: React.FC<{ active: boolean; index: number }> = ({ active, index }) => (
  <section className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden" aria-label="外链推广">
    <DotGrid />
    <SectionNumber n={index + 1} />

    <div className={`relative z-10 w-full transition-all duration-700 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-6 items-center">

        {/* Left: copy + platforms + publish note */}
        <div className="md:col-span-2 pl-8 md:pl-12 space-y-5">
          {/* Header */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-2">外链推广</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-zinc-950 text-balance mb-2">
              AI 智能选题<br />全网图文分发
            </h2>
            <p className="text-sm text-zinc-500 leading-relaxed">
              基于引用分析报告，AI 推荐高引文章主题与最佳投放平台，自动生成图文内容
            </p>
          </div>

          {/* AI flow — gradient orbs + gradient connector */}
          <div className="flex items-center gap-2">
            {[
              { icon: BarChart3, label: '引用分析', from: 'from-emerald-400', to: 'to-teal-500' },
              { icon: Sparkles,  label: 'AI 选题',  from: 'from-teal-400',   to: 'to-cyan-500' },
              { icon: FileCode,  label: '自动成图文', from: 'from-cyan-400',  to: 'to-sky-500'  },
            ].map((step, i) => (
              <React.Fragment key={step.label}>
                <div className="flex flex-col items-center gap-1.5">
                  <div className={`h-9 w-9 rounded-full bg-gradient-to-br ${step.from} ${step.to} flex items-center justify-center shadow-md`}>
                    <step.icon size={15} className="text-white" aria-hidden="true" />
                  </div>
                  <span className="text-[10px] font-medium text-zinc-500 whitespace-nowrap">{step.label}</span>
                </div>
                {i < 2 && (
                  <div className="flex flex-1 items-center gap-0.5 mb-4">
                    <div className="h-px flex-1 bg-gradient-to-r from-teal-200 to-cyan-200" />
                    <ChevronRight size={10} className="text-zinc-300 shrink-0" aria-hidden="true" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Media platforms — floating icons, no card border */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-300 mb-3">支持平台</p>
            <div className="grid grid-cols-5 gap-x-2 gap-y-3">
              {MEDIA_PLATFORMS.map(p => (
                <div key={p.name} className="flex flex-col items-center gap-1">
                  <img
                    src={p.logo}
                    alt={p.name}
                    className="h-8 w-8 object-contain drop-shadow-sm"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  <span className="text-[9px] text-zinc-400 text-center leading-tight">{p.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Auto-publish — larger, highlighted modes */}
          <div className="border-l-2 border-emerald-400 pl-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Zap size={14} className="text-emerald-500 shrink-0" aria-hidden="true" />
              <span className="text-base font-bold text-zinc-800">自动投放</span>
            </div>
            <p className="text-sm text-zinc-500 mb-2">可自动投放至：</p>
            <div className="flex flex-wrap gap-4">
              <span className="flex items-center gap-2 text-sm font-bold text-emerald-600">
                <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" aria-hidden="true" />
                自有账号
              </span>
              <span className="flex items-center gap-2 text-sm font-bold text-teal-600">
                <span className="h-2 w-2 rounded-full bg-teal-500 shrink-0" aria-hidden="true" />
                流量号发文平台
              </span>
            </div>
          </div>
        </div>

        {/* Right: earned media screenshot */}
        <div className="hidden md:col-span-5 md:flex items-center">
          <div className="aspect-video w-full">
            <BrowserFrame url="app.yuanji.ai/earned-media" className="h-full w-full rounded-r-none">
              <div className="flex-1 min-h-0 overflow-hidden">
                <img
                  src="/assets/pitch/earned_page.png"
                  alt="外链推广分析"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </BrowserFrame>
          </div>
        </div>

      </div>
    </div>
  </section>
);

const SiteOptimization: React.FC<{ active: boolean; index: number }> = ({ active, index }) => (
  <section className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden" aria-label="站点优化">
    <DotGrid />
    <SectionNumber n={index + 1} />

    <div className={`relative z-10 w-full transition-all duration-700 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-6 items-center">

        {/* Left: copy + basic + premium cards */}
        <div className="md:col-span-2 pl-8 md:pl-12 space-y-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-2">站点优化</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-zinc-950 text-balance mb-3">
              AI 驱动的<br />全链路站点优化
            </h2>
          </div>

          {/* 基础服务 */}
          <div className="rounded-2xl bg-white p-4 ring-1 ring-zinc-200 shadow-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                <FileCode size={15} aria-hidden="true" />
              </div>
              <div>
                <div className="text-sm font-bold text-zinc-900">基础服务</div>
                <div className="text-[10px] text-zinc-400 mt-0.5">所有套餐均包含</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 mb-2">
              {[
                { icon: Upload, label: '上传源码' },
                { icon: ScanLine, label: '自动分析' },
                { icon: Eye, label: '预览效果' },
              ].map((step, i) => (
                <React.Fragment key={step.label}>
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50">
                      <step.icon size={13} className="text-indigo-500" aria-hidden="true" />
                    </div>
                    <span className="text-[9px] text-zinc-500 whitespace-nowrap">{step.label}</span>
                  </div>
                  {i < 2 && <ArrowRight size={10} className="text-zinc-300 shrink-0 mb-3" aria-hidden="true" />}
                </React.Fragment>
              ))}
            </div>
            <div className="space-y-1.5">
              {[
                { icon: Search, label: 'SEO 自动优化', desc: '标题、描述、结构化数据一键生成' },
                { icon: Bot, label: 'GEO 内容优化', desc: 'AI 可引用的内容结构与关键词布局' },
              ].map(f => (
                <div key={f.label} className="flex items-start gap-2 rounded-lg bg-zinc-50 px-2.5 py-2 ring-1 ring-zinc-100">
                  <f.icon size={12} className="mt-0.5 shrink-0 text-indigo-500" aria-hidden="true" />
                  <div>
                    <div className="text-[11px] font-semibold text-zinc-800">{f.label}</div>
                    <div className="text-[10px] text-zinc-400 mt-0.5">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 增值服务 */}
          <div className="rounded-2xl bg-gradient-to-br from-zinc-950 to-zinc-800 p-4 ring-1 ring-zinc-700">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/10 text-white">
                <Server size={15} aria-hidden="true" />
              </div>
              <div>
                <div className="text-sm font-bold text-white">AI 托管 + 智能网关</div>
                <div className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-2 py-0.5 text-[10px] font-semibold text-amber-300 ring-1 ring-amber-400/30 mt-0.5">
                  <Zap size={8} aria-hidden="true" />
                  增值服务 · 额外订阅
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              {[
                { icon: Cpu, label: 'AI 全自动优化', desc: '网关实时调整站点内容与结构' },
                { icon: GitBranch, label: '请求来源追踪', desc: '精准记录每条请求的 AI 来源' },
                { icon: Shield, label: '企业级安全', desc: 'CDN · 99.9% SLA · DDoS 防护' },
              ].map(f => (
                <div key={f.label} className="flex items-start gap-2 rounded-lg bg-white/5 px-2.5 py-2 ring-1 ring-white/10">
                  <f.icon size={12} className="mt-0.5 shrink-0 text-zinc-300" aria-hidden="true" />
                  <div>
                    <div className="text-[11px] font-semibold text-white">{f.label}</div>
                    <div className="text-[10px] text-zinc-400 mt-0.5">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: gap analysis screenshot */}
        <div className="hidden md:col-span-5 md:flex items-center">
          <div className="aspect-video w-full">
            <BrowserFrame url="app.yuanji.ai/site-optimization" className="h-full w-full rounded-r-none">
              <div className="flex-1 min-h-0 overflow-hidden">
                <img
                  src="/assets/pitch/ai_geo.png"
                  alt="站点优化分析"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </BrowserFrame>
          </div>
        </div>

      </div>
    </div>
  </section>
);

// ─── GeoAgentSection ───────────────────────────────────────────────────────

const GEO_AGENTS = [
  { name: 'Ava',  title: 'Data Analyst',      subtitle: '多引擎检索与参数拆解', icon: Search,   gradient: 'from-sky-500 to-blue-500',     tasks: ['解析用户意图', '执行多引擎搜索', '提取可见性指标'] },
  { name: 'Mia',  title: 'Brand Auditor',      subtitle: '品牌提及与情感聚合',   icon: Brain,    gradient: 'from-emerald-500 to-lime-500',  tasks: ['执行情感分析', '聚合品牌提及', '计算 GEO 得分'] },
  { name: 'Leo',  title: 'Content Strategist', subtitle: '竞品引用与优化机会',   icon: Workflow, gradient: 'from-amber-500 to-orange-500',  tasks: ['分析竞品内容', '识别引用缺口', '优先排序策略'] },
  { name: 'Zane', title: 'Synthesizer',        subtitle: '整合结果输出最终建议', icon: Sparkles, gradient: 'from-violet-500 to-fuchsia-500', tasks: ['交叉引用洞察', '生成引证回复', '输出最终报告'] },
];

// ─── BrandProfile ────────────────────────────────────────────────────────────

const BrandProfile: React.FC<{ active: boolean; index: number }> = ({ active, index }) => (
  <section className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden" aria-label="品牌画像与 AI 可见性">
    <DotGrid />
    <SectionNumber n={index + 1} />

    <div className={`relative z-10 w-full transition-all duration-700 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-6 items-center">

        {/* Left copy */}
        <div className="md:col-span-2 pl-8 md:pl-12 space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-3">品牌画像</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-zinc-950 leading-[1.1] text-balance mb-3">
              输入官网，<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-500 to-pink-500">即刻读懂品牌</span>
            </h2>
            <p className="text-sm text-zinc-500 leading-relaxed">
              输入企业官网后自动解析品牌画像，生成全平台 AI 可见性分析报告；发现定位偏差后可手动调整，精准校准品牌在 AI 中的认知
            </p>
          </div>

          <div className="space-y-2.5">
            {[
              { icon: Globe2,   label: '官网自动解析',     desc: '输入网址，自动提取品牌定位、产品与受众画像' },
              { icon: BarChart3, label: 'AI 可见性报告',   desc: '生成覆盖全平台的引用率与曝光度诊断报告' },
              { icon: Target,   label: '定位偏差校准',     desc: '识别品牌认知偏差，支持手动微调目标定位' },
            ].map(step => (
              <div key={step.label} className="flex items-start gap-3 rounded-xl bg-white p-3.5 ring-1 ring-zinc-100 shadow-card">
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-violet-50 to-violet-100 text-violet-600">
                  <step.icon size={14} aria-hidden="true" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-zinc-800">{step.label}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: brands console */}
        <div className="hidden md:col-span-5 md:block">
          <div className="aspect-video">
            <BrowserFrame url="app.yuanji.ai/brand-profile" className="h-full rounded-r-none">
              <div className="flex-1 min-h-0 overflow-hidden">
                <img src="/assets/pitch/brands.png" alt="品牌画像与 AI 可见性分析" className="w-full h-full object-cover object-top" />
              </div>
            </BrowserFrame>
          </div>
        </div>

      </div>
    </div>
  </section>
);

// ─── GeoAgentSection ──────────────────────────────────────────────────────────

const GeoAgentSection: React.FC<{ active: boolean; index: number }> = ({ active, index }) => (
  <section className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden" aria-label="全自动 GEO Agent">
    <DotGrid />
    <SectionNumber n={index + 1} />

    <div className={`relative z-10 w-full transition-all duration-700 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-6 items-center">

        {/* Left: copy + agent cards */}
        <div className="md:col-span-2 pl-8 md:pl-12 space-y-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-2">GEO Agent</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-zinc-950 text-balance mb-1">
              四大 AI Agent<br />全自动 GEO 优化
            </h2>
            <p className="text-sm text-zinc-500 leading-relaxed">
              并行执行检索、审计、策略与合成，效率提升 4×
            </p>
          </div>

          {/* Agent 2×2 grid */}
          <div className="grid grid-cols-2 gap-2">
            {GEO_AGENTS.map(agent => (
              <div key={agent.name} className="rounded-xl bg-white p-3 ring-1 ring-zinc-200 shadow-card">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={`flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br ${agent.gradient} text-white shrink-0`}>
                    <agent.icon size={13} aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-[11px] font-bold text-zinc-900">{agent.name}</div>
                    <div className="text-[9px] text-zinc-400">{agent.title}</div>
                  </div>
                </div>
                <div className="space-y-0.5">
                  {agent.tasks.map(t => (
                    <div key={t} className="flex items-center gap-1">
                      <span className="h-1 w-1 rounded-full bg-zinc-300 shrink-0" aria-hidden="true" />
                      <span className="text-[9px] text-zinc-500">{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Advantages row */}
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { icon: Brain,    label: '并行处理', desc: '效率提升 4×' },
              { icon: Workflow, label: '专业分工', desc: '深度专业分析' },
              { icon: Sparkles, label: '实时协同', desc: '动态同步推进' },
              { icon: Zap,      label: '智能整合', desc: '输出最优结果' },
            ].map(a => (
              <div key={a.label} className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-2 ring-1 ring-zinc-100 shadow-card">
                <a.icon size={11} className="text-violet-500 shrink-0" aria-hidden="true" />
                <div>
                  <div className="text-[10px] font-semibold text-zinc-800">{a.label}</div>
                  <div className="text-[9px] text-zinc-400">{a.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: geo agent screenshot */}
        <div className="hidden md:col-span-5 md:flex items-center">
          <div className="aspect-video w-full">
            <BrowserFrame url="app.yuanji.ai/geo-agent" className="h-full w-full rounded-r-none">
              <div className="flex-1 min-h-0 overflow-hidden">
                <img
                  src="/assets/pitch/geo_agent.png"
                  alt="GEO Agent 控制台"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </BrowserFrame>
          </div>
        </div>

      </div>
    </div>
  </section>
);

// ─── BehaviorShift ──────────────────────────────────────────────────────────

const BehaviorShift: React.FC<{ active: boolean; index: number }> = ({ active, index }) => {
  const benefits = [
    {
      icon: Users,
      title: '获取高意向客户',
      desc: '潜在客户提问后，直接在 AI 答案中看到你的品牌推荐',
      iconBg: 'bg-indigo-50', iconColor: 'text-indigo-600',
    },
    {
      icon: Zap,
      title: '降低获客成本',
      desc: 'AI 基于语义理解匹配客户潜在需求，替代部分 SEM 投放费用',
      iconBg: 'bg-amber-50', iconColor: 'text-amber-600',
    },
    {
      icon: TrendingUp,
      title: '提高销售转化率',
      desc: 'AI 推荐自带权威属性，信任度远超传统搜索广告和信息流',
      iconBg: 'bg-sky-50', iconColor: 'text-sky-600',
    },
    {
      icon: Brain,
      title: '抢占客户心智',
      desc: '反复被 AI 推荐后，客户对品牌的信任度潜移默化地形成',
      iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600',
    },
  ];

  const outcomes = [
    { num: '60%',    label: '推荐客户精准度比传统渠道高', color: 'from-indigo-500 to-violet-500' },
    { num: '50%',    label: '获客成本降低',               color: 'from-violet-500 to-fuchsia-500' },
    { num: '5–10×',  label: '客户咨询的销售转化率',       color: 'from-cyan-500 to-sky-500' },
    { num: '饱和营销', label: '客户选择品牌的最后一站',   color: 'from-indigo-500 to-cyan-500' },
  ];

  return (
    <SectionShell index={index} active={active} label="用户行为迁徙">
      {/* Header */}
      <div className="mb-7 text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-2">用户行为迁徙</p>
        <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-zinc-950 leading-[1.05]">
          AI 正在重写
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-violet-500 to-cyan-500"> 用户决策路径</span>
        </h2>
        <p className="mt-2 text-zinc-400 text-sm">品牌在 AI 时代获客的四大核心价值</p>
      </div>

      {/* 4 benefit cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        {benefits.map(b => (
          <div key={b.title} className="rounded-2xl bg-zinc-50 ring-1 ring-zinc-100 p-4">
            <div className={`h-8 w-8 rounded-xl ${b.iconBg} flex items-center justify-center mb-3`}>
              <b.icon size={15} className={b.iconColor} aria-hidden="true" />
            </div>
            <div className="text-[13px] font-bold text-zinc-900 mb-1.5">{b.title}</div>
            <div className="text-[11px] text-zinc-500 leading-relaxed">{b.desc}</div>
          </div>
        ))}
      </div>

      {/* 4 outcome stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {outcomes.map(o => (
          <div key={o.label} className="rounded-2xl bg-zinc-50 ring-1 ring-zinc-100 p-4 text-center">
            <div className={`text-2xl font-black tracking-tight mb-1 text-transparent bg-clip-text bg-gradient-to-r ${o.color}`}>
              {o.num}
            </div>
            <div className="text-[11px] text-zinc-500 leading-relaxed">{o.label}</div>
          </div>
        ))}
      </div>

      {/* Bottom insight */}
      <div className="relative overflow-hidden rounded-[28px] bg-[linear-gradient(120deg,rgba(250,250,255,0.96)_0%,rgba(239,242,255,0.94)_48%,rgba(245,243,255,0.98)_100%)] px-5 py-4 shadow-[0_24px_60px_-30px_rgba(79,70,229,0.35)]">
        <div className="absolute -right-12 -top-10 h-32 w-32 rounded-full bg-indigo-300/20 blur-3xl" aria-hidden="true" />
        <div className="absolute left-16 top-1/2 h-20 w-20 -translate-y-1/2 rounded-full bg-violet-200/20 blur-2xl" aria-hidden="true" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.42)_0%,rgba(255,255,255,0)_48%,rgba(255,255,255,0.2)_100%)]" aria-hidden="true" />

        <div className="relative flex items-center gap-4">
          <div className="shrink-0 flex h-11 w-11 items-center justify-center rounded-[18px] bg-white/55 shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_10px_30px_-18px_rgba(79,70,229,0.6)] backdrop-blur-md">
            <Zap size={18} className="text-indigo-600" aria-hidden="true" />
          </div>

          <p className="text-sm leading-relaxed text-zinc-600 md:text-[15px]">
            <span className="font-semibold text-zinc-950">行动窗口正在关闭：</span>
            <span className="text-zinc-600/95"> SEO 时代的流量逻辑已经失效。率先建立 AI 可见度优势的品牌，将掌握</span>
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-500 bg-clip-text font-semibold text-transparent"> 下一代用户注意力入口</span>
            <span className="text-zinc-600/95">。</span>
          </p>
        </div>
      </div>
    </SectionShell>
  );
};

// ─── MarketSize ──────────────────────────────────────────────────────────────

const MarketSize: React.FC<{ active: boolean; index: number }> = ({ active, index }) => {
  const logics = [
    {
      icon: Users,
      num: '01',
      stat: '5.15亿',
      unit: '中国 AI 用户',
      title: '用户决策迁徙已完成',
      sub: '60%+ 消费者直接依据 AI 推荐购买',
      iconBg: 'bg-sky-50 ring-1 ring-sky-100',
      iconColor: 'text-sky-600',
      statColor: 'text-sky-600',
    },
    {
      icon: Brain,
      num: '02',
      stat: '↑ 3×',
      unit: 'LLM 多源采信率',
      title: '信源密度即护城河',
      sub: '多域名背书远胜单点关键词优化',
      iconBg: 'bg-violet-50 ring-1 ring-violet-100',
      iconColor: 'text-violet-600',
      statColor: 'text-violet-600',
    },
    {
      icon: TrendingUp,
      num: '03',
      stat: '86×',
      unit: '5 年市场增幅',
      title: '指数级爆发确定性高',
      sub: '艾瑞咨询预测 2030 年 518 亿元',
      iconBg: 'bg-emerald-50 ring-1 ring-emerald-100',
      iconColor: 'text-emerald-600',
      statColor: 'text-emerald-600',
    },
  ];

  const forecast = [
    { year: '2025', amount: '6亿',     source: '艾瑞咨询',   pct: 8,   barColor: 'bg-sky-400' },
    { year: '2026', amount: '30亿',    source: '易观',        pct: 18,  barColor: 'bg-sky-500' },
    { year: '2026', amount: '286亿',   source: '中国信通院',  pct: 44,  barColor: 'bg-indigo-500' },
    { year: '2030', amount: '518亿',   source: '艾瑞咨询',    pct: 66,  barColor: 'bg-violet-500' },
    { year: '2030', amount: '5000亿+', source: '凤凰网·广义', pct: 100, barColor: 'bg-gradient-to-r from-violet-500 to-fuchsia-400' },
  ];

  return (
    <section
      className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden px-6"
      aria-label="市场规模与发展前景"
    >
      <DotGrid />
      <SectionNumber n={index + 1} />

      <div className={`relative z-10 w-full max-w-6xl transition-all duration-700 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>

        {/* Title row */}
        <div className="mb-5 flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-1.5">市场规模与发展前景</p>
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-zinc-950">
              3 个底层逻辑驱动
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-violet-500"> 行业爆发</span>
            </h2>
          </div>
          <div className="text-right hidden md:block">
            <div className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-violet-500">50×+</div>
            <div className="text-[11px] text-zinc-400 mt-0.5">5 年确定性增长空间</div>
          </div>
        </div>

        {/* 3 stat cards – horizontal row */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {logics.map(l => (
            <div key={l.num} className="rounded-2xl bg-white ring-1 ring-zinc-100 shadow-sm p-4 flex gap-3">
              <div className={`shrink-0 h-8 w-8 rounded-xl ${l.iconBg} flex items-center justify-center`}>
                <l.icon size={14} className={l.iconColor} aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="flex items-baseline gap-1.5 mb-0.5">
                  <span className={`text-2xl font-black leading-none tracking-tight ${l.statColor}`}>{l.stat}</span>
                  <span className="text-[9px] font-semibold text-zinc-400">{l.unit}</span>
                </div>
                <div className="text-[12px] font-bold text-zinc-800 mb-0.5">{l.title}</div>
                <div className="text-[10px] text-zinc-500 leading-relaxed">{l.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom: animated bar chart + conclusion */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">

          {/* Bar chart */}
          <div className="md:col-span-3 rounded-2xl bg-white ring-1 ring-zinc-100 shadow-sm p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-3.5">
              各机构预测 · 中国 GEO 市场规模
            </div>
            <div className="space-y-2.5">
              {forecast.map((f, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <span className="w-8 shrink-0 text-[10px] font-bold text-zinc-400 text-right tabular-nums">{f.year}</span>
                  <div className="flex-1 h-5 rounded-full bg-zinc-50 relative overflow-hidden">
                    <div
                      className={`absolute inset-y-0 left-0 rounded-full ${f.barColor}`}
                      style={{
                        width: active ? `${f.pct}%` : '2%',
                        transition: 'width 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
                        transitionDelay: `${i * 110 + 200}ms`,
                      }}
                    />
                  </div>
                  <span className="w-16 shrink-0 text-[11px] font-black text-zinc-700 tabular-nums">{f.amount}</span>
                  <span className="w-20 shrink-0 text-[9px] text-zinc-400 leading-tight">{f.source}</span>
                </div>
              ))}
            </div>
            <div className="mt-3.5 pt-3 border-t border-zinc-50 flex items-start gap-1.5 text-[9px] text-zinc-400 leading-relaxed">
              <div className="mt-1 h-1 w-1 rounded-full bg-zinc-300 shrink-0" />
              来源：易观·中国信通院·艾瑞咨询·IDC/国信证券·凤凰网；各机构口径不同，绝对数值仅供参考
            </div>
          </div>

          {/* Conclusion card */}
          <div className="md:col-span-2 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 p-5 flex flex-col justify-between">
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wider text-indigo-200 mb-3">业内公认 · 中位数判断</div>
              <div className="text-indigo-100 text-xs mb-1.5">2026 年中国 GEO 纯服务收入</div>
              <div className="text-yellow-300 text-3xl font-black tracking-tight leading-none">30 – 300亿</div>
              <div className="text-indigo-200 text-sm mt-2.5">
                5 年内确定性 <span className="text-white font-bold text-base">50×</span> 以上增长
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {[
                'IDC 全球三年复合增长率 122%',
                'Gartner：2028 年 50% 搜索流量被 AI 取代',
                '现阶段中国 GEO 服务商不超过 50 家',
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="h-1 w-1 rounded-full bg-indigo-300 shrink-0" />
                  <span className="text-[10px] text-indigo-200 leading-tight">{text}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

// ─── FeatureHighlights ───────────────────────────────────────────────────────

const FeatureHighlights: React.FC<{ active: boolean; index: number }> = ({ active, index }) => {
  const features = [
    { icon: Search, title: 'AI 可见度诊断', desc: '多维度评估全网 AI 引擎中的品牌曝光率，精准挖掘流量洼地', color: 'text-sky-600', bg: 'bg-sky-50' },
    { icon: BarChart3, title: '竞品对比分析', desc: '实时追踪竞品在各 AI 平台的排名与提及趋势，知己知彼', color: 'text-violet-600', bg: 'bg-violet-50' },
    { icon: Target, title: '精准渠道投放', desc: '元基引擎锁定高价值发布渠道，最低成本换取最大曝光增长', color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { icon: Layers, title: 'AI 图文工厂', desc: '批量生成专属内容，兼顾矩阵外链铺发与深度精品长文创作', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { icon: MonitorSmartphone, title: 'GEO Agent 自动化', desc: '全套工作流一键调度，从诊断到分发的完整 GEO 闭环', color: 'text-orange-500', bg: 'bg-orange-50' },
    { icon: Network, title: '多品牌多站点', desc: '统一管理多个品牌和站点，跨账户汇总可见度数据', color: 'text-cyan-600', bg: 'bg-cyan-50' },
    { icon: TrendingUp, title: '效果归因报告', desc: '清晰展示每项优化措施带来的可见度变化，ROI 一目了然', color: 'text-rose-500', bg: 'bg-rose-50' },
    { icon: Users, title: '团队协作', desc: '多成员工作区、权限分级，支持企业级团队协同工作', color: 'text-zinc-600', bg: 'bg-zinc-50' },
    { icon: LineChart, title: '自定义仪表盘', desc: '按需配置监控面板，将最重要的指标放在最显眼的位置', color: 'text-teal-600', bg: 'bg-teal-50' },
  ];

  return (
    <section className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden px-6" aria-label="功能亮点">
      <DotGrid />
      <SectionNumber n={index + 1} />

      <div className={`relative z-10 w-full max-w-5xl transition-all duration-700 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="mb-7 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-2">功能亮点</p>
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-zinc-950">
            一平台 · 全链路 ·
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-violet-500"> 全托管</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {features.map(f => (
            <div key={f.title} className="rounded-2xl bg-white ring-1 ring-zinc-100 shadow-sm p-4">
              <div className={`h-8 w-8 rounded-xl ${f.bg} flex items-center justify-center mb-2.5`}>
                <f.icon size={15} className={f.color} aria-hidden="true" />
              </div>
              <div className="text-[13px] font-bold text-zinc-800 mb-1">{f.title}</div>
              <div className="text-[11px] text-zinc-500 leading-relaxed">{f.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── CallToAction ────────────────────────────────────────────────────────────

const CallToAction: React.FC<{ active: boolean; index: number; onEnterApp?: () => void }> = ({ active, index, onEnterApp }) => (
  <section className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden px-6" aria-label="开始使用">
    <DotGrid />
    <GradientOrbs />
    <SectionNumber n={index + 1} />

    <div className={`relative z-10 flex flex-col items-center text-center max-w-3xl transition-all duration-700 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-zinc-50 px-4 py-1.5 text-xs font-medium text-zinc-600 ring-1 ring-zinc-200">
        <Sparkles size={12} className="text-indigo-500" aria-hidden="true" />
        现在加入，锁定市场先机
      </div>

      <h2 className="text-5xl md:text-6xl font-black tracking-tighter text-zinc-950 mb-4 leading-[1.05]">
        现在开始
        <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-violet-500 to-cyan-500">
          建立 AI 时代护城河
        </span>
      </h2>

      <p className="text-zinc-500 text-base mb-8 max-w-lg leading-relaxed">
        GEO 市场窗口正在关闭。率先布局的品牌将占据 AI 搜索推荐的黄金位置，而等待者将面临越来越高的追赶成本。
      </p>

      <div className="flex flex-col sm:flex-row items-center gap-3">
        <button
          onClick={onEnterApp}
          className="inline-flex items-center gap-2 rounded-full bg-zinc-950 px-7 py-3.5 text-sm font-semibold text-white shadow-lg hover:bg-zinc-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-2"
          aria-label="立即体验 PEEC AI"
        >
          立即体验
          <ArrowRight size={15} aria-hidden="true" />
        </button>
      </div>

      <div className="mt-12 flex items-center gap-8">
        {[
          { num: '10,000+', label: '已监控关键词' },
          { num: '50+', label: 'AI 平台覆盖' },
          { num: '99.9%', label: '服务可用性' },
        ].map(stat => (
          <div key={stat.label} className="text-center">
            <div className="text-2xl font-black text-zinc-900">{stat.num}</div>
            <div className="text-xs text-zinc-400 mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── LandingPage ─────────────────────────────────────────────────────────────

const LandingPage: React.FC<LandingPageProps> = ({ onEnterApp }) => {
  const [current, setCurrent] = useState(0);
  const [navVisible, setNavVisible] = useState(false);
  const isAnimating = useRef(false);
  const touchStartY = useRef<number | null>(null);

  const navigate = useCallback((next: number) => {
    const clamped = Math.max(0, Math.min(TOTAL_SECTIONS - 1, next));
    if (clamped === current || isAnimating.current) return;
    isAnimating.current = true;
    setCurrent(clamped);
    setTimeout(() => { isAnimating.current = false; }, 800);
  }, [current]);

  useEffect(() => {
    const timer = setTimeout(() => setNavVisible(true), 600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') navigate(current + 1);
      if (e.key === 'ArrowUp' || e.key === 'PageUp') navigate(current - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, navigate]);

  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (Math.abs(e.deltaY) < 30) return;
      navigate(current + (e.deltaY > 0 ? 1 : -1));
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [current, navigate]);

  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => { touchStartY.current = e.touches[0].clientY; };
    const onTouchEnd = (e: TouchEvent) => {
      if (touchStartY.current === null) return;
      const delta = touchStartY.current - e.changedTouches[0].clientY;
      if (Math.abs(delta) > 50) navigate(current + (delta > 0 ? 1 : -1));
      touchStartY.current = null;
    };
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [current, navigate]);

  return (
    <div className="relative h-screen w-full overflow-hidden bg-white">
      {/* Sections — translate vertically to show active section */}
      <div
        className="absolute inset-x-0 top-0 transition-transform duration-700 ease-in-out"
        style={{ transform: `translateY(-${current * 100}vh)` }}
      >
        <Hero active={current === 0} onEnterApp={onEnterApp} onNext={() => navigate(1)} />
        <BehaviorShift active={current === 1} index={1} />
        <MarketSize active={current === 2} index={2} />
        <CustomerSegments active={current === 3} index={3} />
        <PlatformMonitoring active={current === 4} index={4} />
        <IntelligenceEngine active={current === 5} index={5} />
        <EarnedMedia active={current === 6} index={6} />
        <SiteOptimization active={current === 7} index={7} />
        <BrandProfile active={current === 8} index={8} />
        <GeoAgentSection active={current === 9} index={9} />
        <FeatureHighlights active={current === 10} index={10} />
        <CallToAction active={current === 11} index={11} onEnterApp={onEnterApp} />
      </div>

      {/* Top nav */}
      <nav
        className={`absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-8 py-4 transition-all duration-500 ${navVisible ? 'opacity-100' : 'opacity-0'}`}
        aria-label="页面导航"
      >
        <div className="flex items-center gap-1.5 leading-none select-none">
            <span className="text-lg font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-zinc-800 to-zinc-500">
              元基
            </span>
            <span className="text-lg font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-cyan-500">
              GEO
            </span>
            <span className="inline-flex items-center justify-center rounded-bl-lg rounded-br-sm rounded-tl-sm rounded-tr-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 px-1.5 py-1 text-[9px] font-bold uppercase leading-none text-white shadow-sm ring-1 ring-inset ring-white/20">
              AI
            </span>
          </div>
        <div className="hidden md:flex items-center gap-1">
          {SECTION_LABELS.slice(1).map((label, i) => (
            <button
              key={label}
              onClick={() => navigate(i + 1)}
              className={`px-3 py-1.5 rounded-full text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-1 ${current === i + 1 ? 'bg-zinc-950 text-white font-semibold' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'}`}
              aria-label={`跳转到 ${label}`}
              aria-current={current === i + 1 ? 'true' : undefined}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={onEnterApp}
          className="inline-flex items-center text-[13px] font-medium text-zinc-600 hover:text-zinc-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-1 rounded-md px-2 py-1"
          aria-label="进入应用"
        >
          立即体验 <ArrowRight size={13} className="ml-1" aria-hidden="true" />
        </button>
      </nav>

      {/* Dot navigation */}
      <div
        className={`absolute right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-2 transition-all duration-500 ${navVisible ? 'opacity-100' : 'opacity-0'}`}
        role="navigation"
        aria-label="章节指示器"
      >
        {Array.from({ length: TOTAL_SECTIONS }).map((_, i) => (
          <button
            key={i}
            onClick={() => navigate(i)}
            className={`rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-1 ${current === i ? 'h-4 w-2 bg-zinc-950' : 'h-2 w-2 bg-zinc-300 hover:bg-zinc-500'}`}
            aria-label={`跳转到 ${SECTION_LABELS[i]}`}
            aria-current={current === i ? 'true' : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export default LandingPage;
